import {
  ClientContentItemEntity,
  ClientContentOptimizationDraft,
  ClientContentOptimizationEntity,
  ClientContentOptimizationTypeEntity,
  ISyncCoreApi,
} from '@edgebox/sync-core-rest-client';
import { ExternalServiceId, InternalId, StaticReference, StaticRevisionReference } from '@edgebox/data-definition-kit';
import {
  ContentExtractionType,
  ContentItemStatus,
  ContentOptimizationStatus,
  ContentOptimizationTargetStage,
  ContentPriority,
  ContentPrioritySource,
  ContentRecommendationKind,
  ContentRepresentation,
  IContentExtractionDescriptorRuntime,
  ISyncCoreSite,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import { Alert } from '@edgebox/react-components';
import { faArrowLeft, faArrowUp, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { ContentItemDetailSkeleton } from './Components/ContentItemDetailSkeleton';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useNavigate, useParams } from 'react-router';
import { SwitchButton } from '../../SwitchButton';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { searchOfBrandPresenceScreenAddress, useBrandPresenceScreenAddress } from './brandPresenceScreenQuery';
import { ContentItemStatusIcon } from './Onboarding/ContentInventory/Components/ContentItemStatusIcon';
import { HealthCheckNameReference } from './Onboarding/ContentInventory/Components/HealthCheckNameReference';
import { loadHealthCheckReferenceDataByKey } from './Onboarding/ContentInventory/Components/HealthCheckReferenceData';
import { resolveHealthCheckScorePresentation } from './Onboarding/ContentInventory/Components/HealthCheckScorePresentation';
import { resolveTaxonomyFacetPresentation, TaxonomyTermNameBadge } from './Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { DEFAULT_MARKDOWN_CONTENT_CLASS_NAME, MarkdownContentBox } from './Components/MarkdownContentBox';
import {
  BRAND_PRESENCE_TAB_IN_STRIP_CLASSES,
  getBrandPresenceSubTabClassName,
  getBrandPresenceTabClassName,
} from './Components/BrandPresenceTabStyles';
import { BrandPresenceTabStrip } from './Components/BrandPresenceTabStrip';
import { ContentItemKpiCards } from './Components/ContentItemKpiCards';
import { COUNT_ONLY } from './Components/GovernanceTermUsageCards';
import { ContentItemPageFigures } from './Components/ContentItemPageFigures';
import { ContentItemGapsTable } from './Components/ContentItemGapsTable';
import { toComparableContents } from './Components/contentBlocks';
import { SiteDraftReviewNotice } from './Components/SiteDraftReviewNotice';
import { loadDraftOfOptimizedPage, loadOptimizationForSuggestion } from './Components/optimizationForSuggestion.client';
import { toAbsoluteUrl } from './Onboarding/components/governanceOnboardingUrlSelection';
import { ISiteDraftNotice, SITE_DRAFT_REVIEW_BODY_IN_APP_COPY, toSiteDraftNotice } from './Components/siteDraftNotice';
import { FaqDraftFollower } from './Components/FaqDraftFollower';
import { FaqPanel } from './Components/FaqPanel';
import { useSuggestedFaqs } from './Components/useSuggestedFaqs';
import { loadTaxonomyTerm, loadTaxonomyTermText } from './Components/taxonomyTermText.client';
import {
  BRAND_PRESENCE_CONTENT_INVENTORY_PATH,
  BRAND_PRESENCE_CONTENT_ITEM_HEALTH_TAB_TOKEN,
  BRAND_PRESENCE_CONTENT_ITEM_QUERY_PARAMS,
  brandPresenceContentItemPath,
  brandPresenceContentItemStatusFromSegment,
} from './brandPresencePaths';
import { BrandPresenceEyebrow } from './Components/BrandPresencePageHeader';
import { BrandPresenceEmptyState } from './Components/BrandPresenceEmptyState';
import { SkeletonBlock, SkeletonRegion } from './Components/BrandPresenceSkeleton';
import { BP_CARD_CLASSES, BP_TOOLBAR_BUTTON_CLASSES } from './Components/brandPresenceClasses';

type GapRow = {
  healthCheckKey: string;
  score: number | null;
  issueId?: string;
  computedAtTimestamp?: number;
};

type DetailTabKey = 'gaps' | 'history' | 'content' | 'faq' | 'optimization';
type OptimizationContentTabKey = 'html' | 'markdown';
type OptimizationMarkdownMode = 'visual' | 'technical';

type ContentHistoryEntry = {
  id: string;
  headline: string;
  timestamp?: number;
  description: string;
  scoreDelta?: number;
};

const HISTORY_ITEMS_PER_PAGE = 5;

/** The shape of a text while it loads: a heading and a few lines of uneven length. */
const TEXT_SKELETON = (
  <>
    <SkeletonBlock className="h-5 w-2/5" />
    <SkeletonBlock className="h-3.5 w-full" />
    <SkeletonBlock className="h-3.5 w-11/12" />
    <SkeletonBlock className="h-3.5 w-4/5" />
    <SkeletonBlock className="mt-5 h-5 w-1/3" />
    <SkeletonBlock className="h-3.5 w-full" />
    <SkeletonBlock className="h-3.5 w-3/4" />
  </>
);

/** The shape of the history while it loads: dated entries down a timeline. */
const HISTORY_SKELETON = [0, 1, 2].map((entry) => (
  <div key={entry} className="grid grid-cols-[24px,1fr] gap-4">
    <SkeletonBlock className="mt-1.5 h-3 w-3 rounded-full" />
    <div className="space-y-2 rounded-xl border border-slate-200/80 p-4">
      <SkeletonBlock className="h-4 w-1/3" />
      <SkeletonBlock className="h-3.5 w-3/4" />
    </div>
  </div>
));

const issueFirstDetectedCache = new Map<string, string>();
const issueFirstDetectedRequestCache = new Map<string, Promise<string>>();

function toText(value: unknown, fallback = '-'): string {
  if (typeof value === 'string') {
    const text = value.trim();
    return text.length ? text : fallback;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return fallback;
}

function translateEnumSafe(
  enumName: 'ContentPriority' | 'ContentItemStatus' | 'ContentWorkflowState',
  value: unknown,
  fallback = '-'
): string {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(enumName, value as never);
  } catch {
    return toText(value, fallback);
  }
}

function toStableKey(reference: unknown): string | undefined {
  if (!reference) {
    return undefined;
  }

  if (typeof reference === 'string') {
    const key = reference.trim();
    return key.length ? key : undefined;
  }

  if (typeof reference !== 'object') {
    return undefined;
  }

  const candidate = reference as {
    getStableKey?: () => string | undefined;
    stableKey?: string;
    key?: string;
  };

  const methodKey = candidate.getStableKey?.();
  if (typeof methodKey === 'string' && methodKey.trim().length > 0) {
    return methodKey;
  }

  if (typeof candidate.stableKey === 'string' && candidate.stableKey.trim().length > 0) {
    return candidate.stableKey;
  }

  if (typeof candidate.key === 'string' && candidate.key.trim().length > 0) {
    return candidate.key;
  }

  return undefined;
}

function toInternalId(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    const id = value.trim();
    return id.length ? id : undefined;
  }

  if (typeof value !== 'object') {
    return undefined;
  }

  const candidate = value as {
    getId?: () => string | undefined;
    id?: string;
  };

  const runtimeId = candidate.getId?.();
  if (typeof runtimeId === 'string' && runtimeId.trim().length > 0) {
    return runtimeId;
  }

  if (typeof candidate.id === 'string' && candidate.id.trim().length > 0) {
    return candidate.id;
  }

  return undefined;
}

function toInternalEntityId(value: unknown): InternalId | undefined {
  const id = toInternalId(value);
  return id ? (id as InternalId) : undefined;
}

function decodeContentItemKey(rawKey: string | undefined): string | undefined {
  if (!rawKey) {
    return undefined;
  }

  if (!rawKey.includes('%')) {
    return rawKey;
  }

  try {
    return decodeURIComponent(rawKey);
  } catch {
    return rawKey;
  }
}

function toScorePercentage(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }

  return Math.round(Math.max(0, Math.min(1, value)) * 100);
}

function toQualityDeltaScaleScore(deltaPercentage: number): number {
  if (!Number.isFinite(deltaPercentage)) {
    return 50;
  }

  if (deltaPercentage >= 0) {
    return 100;
  }

  const shiftedDelta = deltaPercentage - 10;

  if (shiftedDelta <= -20) {
    return 50;
  }

  const normalized = 1 - Math.abs(shiftedDelta) / 20;
  return 50 + normalized * 50;
}

type AssignedTermModel = {
  termKey: string;
  facet?: TaxonomyFacet;
};

function areStringRecordsEqual(left: Record<string, string>, right: Record<string, string>): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  for (const key of leftKeys) {
    if (left[key] !== right[key]) {
      return false;
    }
  }

  return true;
}

function areNumberRecordsEqual(left: Record<string, number>, right: Record<string, number>): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  for (const key of leftKeys) {
    if (left[key] !== right[key]) {
      return false;
    }
  }

  return true;
}

function toTaxonomyFacet(value: unknown): TaxonomyFacet | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const facets = Object.values(TaxonomyFacet) as string[];
  if (!facets.includes(value)) {
    return undefined;
  }

  return value as TaxonomyFacet;
}

function toDateLabel(timestamp?: number): string {
  if (!timestamp || !Number.isFinite(timestamp)) {
    return '-';
  }

  return new Date(timestamp).toLocaleDateString();
}

function toDateTimeLabel(timestamp?: number): string {
  if (!timestamp || !Number.isFinite(timestamp)) {
    return '-';
  }

  return new Date(timestamp).toLocaleString();
}

function formatAgeFromNow(timestamp?: number): string {
  if (!timestamp || !Number.isFinite(timestamp)) {
    return '-';
  }

  return moment(timestamp).fromNow();
}

function toTimestamp(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value && typeof value === 'object') {
    const maybeWithValueOf = value as { valueOf?: () => unknown };
    const extracted = maybeWithValueOf.valueOf?.();
    if (typeof extracted === 'number' && Number.isFinite(extracted)) {
      return extracted;
    }
  }

  return undefined;
}

/**
 * When the figures above the tabs were last measured: the newest of the moments that produced them —
 * each health check's computation, the citation count's, and the crawl that last read the page.
 *
 * A page none of the three has touched has had nothing measured on it and carries no date at all, so
 * the line stays off the screen rather than dating figures that were never taken by the moment
 * somebody happened to look at them.
 */
function figuresUpdatedTimestamp(item: ClientContentItemEntity | undefined): number | undefined {
  const measured = [...(item?.health || []).map((entry) => entry?.computedAt), item?.citationCountAt, item?.lastCrawledAt]
    .map((at) => toTimestamp(at))
    .filter((timestamp): timestamp is number => timestamp !== undefined);

  return measured.length ? Math.max(...measured) : undefined;
}

function normalizeContentItemKey(key?: string): string {
  return (decodeContentItemKey(key) || '').trim();
}

function appendUniqueContentItems(existing: ClientContentItemEntity[], incoming: ClientContentItemEntity[]): ClientContentItemEntity[] {
  if (!incoming.length) {
    return existing;
  }

  const uniqueById = new Map<string, ClientContentItemEntity>();
  for (const item of existing) {
    const id = toInternalId(item.id) || '';
    if (id) {
      uniqueById.set(id, item);
    }
  }

  for (const item of incoming) {
    const id = toInternalId(item.id) || '';
    if (id && !uniqueById.has(id)) {
      uniqueById.set(id, item);
    }
  }

  return Array.from(uniqueById.values());
}

async function loadContentItemRevisionHistoryPage(
  api: ISyncCoreApi,
  contentItemKey: string,
  page: number
): Promise<{ matches: ClientContentItemEntity[]; nextPage: number; hasMore: boolean }> {
  const normalizedKey = normalizeContentItemKey(contentItemKey);
  if (!normalizedKey) {
    return {
      matches: [],
      nextPage: page,
      hasMore: false,
    };
  }
  const targetPage = Math.max(0, page);
  const response = await api.governance.contentItems.revisionsByPreviousStatus(normalizedKey, ContentItemStatus.Published, {
    page: targetPage,
    itemsPerPage: HISTORY_ITEMS_PER_PAGE,
  });
  const lastPage = response.numberOfPages <= 0 || response.page >= response.numberOfPages - 1;

  return {
    matches: response.items,
    nextPage: targetPage + 1,
    hasMore: !lastPage,
  };
}

async function loadHtmlContentByRevisionId(api: ISyncCoreApi, item: ClientContentItemEntity): Promise<string> {
  const revisionId = toInternalEntityId(item.id);
  if (!revisionId) {
    return '';
  }

  return api.governance.contentItems.htmlContent(revisionId);
}

async function loadMarkdownContentByExtraction(item: ClientContentItemEntity): Promise<string> {
  const markdownExtractionDescriptor: IContentExtractionDescriptorRuntime | undefined = item.extractions?.[ContentExtractionType.Markdown];

  const file = await markdownExtractionDescriptor?.file?.get?.();
  const downloadUrl = file?.downloadUrl;
  if (!downloadUrl) {
    throw new Error('Markdown extraction file is not available.');
  }

  const response = await fetch(downloadUrl);
  return response.text();
}

/** The suggestion revision of one page, with its full body, or undefined when the page has none. */
async function loadSuggestionRevisionByKey(api: ISyncCoreApi, contentItemKey: string): Promise<ClientContentItemEntity | undefined> {
  const suggestionRevision = await loadDraftOfOptimizedPage(api, { pageKey: contentItemKey });
  if (!suggestionRevision) {
    return undefined;
  }

  return api.governance.contentItems.item(suggestionRevision.id);
}

function buildContentHistoryRows(item: ClientContentItemEntity | undefined, revisions: ClientContentItemEntity[]): ContentHistoryEntry[] {
  if (!item && !revisions.length) {
    return [];
  }

  const snapshots = [...(item ? [item] : []), ...revisions];

  const rows = snapshots.map((snapshot, index) => {
    const isLatest = item ? toInternalId(snapshot.id) === toInternalId(item.id) : index === 0;
    const statusLabel = translateEnumSafe('ContentItemStatus', snapshot.status);
    const previousStatusLabel = translateEnumSafe('ContentItemStatus', snapshot.previousStatus, 'unknown');
    const scorePercentage = toScorePercentage(snapshot.healthScore);

    return {
      id: toInternalId(snapshot.id) || `${snapshot.key}:${snapshot.version}`,
      headline: isLatest ? 'Latest snapshot' : 'Previous revision',
      timestamp:
        toTimestamp(snapshot.fetchedAt) ??
        toTimestamp(snapshot.lastCrawledAt) ??
        toTimestamp(snapshot.updatedAt) ??
        toTimestamp(snapshot.createdAt),
      description: isLatest
        ? `Current status: ${statusLabel}${snapshot.version ? ` (v${snapshot.version})` : ''}.`
        : `Archived from ${previousStatusLabel} as ${statusLabel}${snapshot.version ? ` (v${snapshot.version})` : ''}.`,
      scoreDelta: undefined as number | undefined,
      scorePercentage,
    };
  });

  for (let index = 0; index < rows.length - 1; index += 1) {
    const current = rows[index];
    const previous = rows[index + 1];

    if (current.scorePercentage === undefined || previous.scorePercentage === undefined) {
      continue;
    }

    current.scoreDelta = current.scorePercentage - previous.scorePercentage;
  }

  return rows.map(({ scorePercentage: _scorePercentage, ...row }) => row);
}

type GovernanceIssueApi = {
  item: (id: string) => Promise<unknown>;
};

function resolveGovernanceIssueApi(api: ISyncCoreApi | undefined): GovernanceIssueApi | undefined {
  if (!api) {
    return undefined;
  }

  const governance = (api as unknown as { governance?: Record<string, unknown> }).governance;
  const maybeApi = governance?.issue ?? governance?.issues;
  if (!maybeApi || typeof maybeApi !== 'object') {
    return undefined;
  }

  const typed = maybeApi as { item?: (id: string) => Promise<unknown> };
  if (typeof typed.item !== 'function') {
    return undefined;
  }

  return { item: typed.item };
}

async function loadIssueFirstDetectedLabel(api: ISyncCoreApi | undefined, issueId: string): Promise<string> {
  const key = issueId.trim();
  if (!key) {
    return '-';
  }

  const cached = issueFirstDetectedCache.get(key);
  if (cached) {
    return cached;
  }

  const pending = issueFirstDetectedRequestCache.get(key);
  if (pending) {
    return pending;
  }

  const issueApi = resolveGovernanceIssueApi(api);
  if (!issueApi) {
    return '-';
  }

  const request = issueApi
    .item(key)
    .then((issue) => {
      const candidate = issue as {
        createdAt?: unknown;
        firstDetectedAt?: unknown;
        detectedAt?: unknown;
        updatedAt?: unknown;
      };

      const timestamp =
        toTimestamp(candidate.firstDetectedAt) ??
        toTimestamp(candidate.detectedAt) ??
        toTimestamp(candidate.createdAt) ??
        toTimestamp(candidate.updatedAt);

      return formatAgeFromNow(timestamp);
    })
    .catch(() => '-')
    .finally(() => {
      issueFirstDetectedRequestCache.delete(key);
    });

  issueFirstDetectedRequestCache.set(key, request);

  const label = await request;
  issueFirstDetectedCache.set(key, label);
  return label;
}

function IssueFirstDetectedReference({ api, issueId }: { api?: ISyncCoreApi; issueId?: string }) {
  const [label, setLabel] = React.useState<string>('—');

  React.useEffect(() => {
    if (!issueId) {
      setLabel('—');
      return;
    }

    let active = true;

    void (async () => {
      const loadedLabel = await loadIssueFirstDetectedLabel(api, issueId);
      if (!active) {
        return;
      }

      setLabel(loadedLabel);
    })();

    return () => {
      active = false;
    };
  }, [api, issueId]);

  return <>{label}</>;
}

function toAllHealthRows(item: ClientContentItemEntity | undefined): GapRow[] {
  if (!item) {
    return [];
  }

  const healthEntries = item.health || [];

  return healthEntries
    .map((entry): GapRow | undefined => {
      if (!entry || typeof entry !== 'object') {
        return undefined;
      }

      const score = typeof entry.score === 'number' && Number.isFinite(entry.score) ? entry.score : null;
      const healthCheckKey = toStableKey(entry.healthCheck);
      const issueId = toInternalId(entry.issueId);
      const computedAt = entry.computedAt;
      const computedAtTimestamp = computedAt?.valueOf?.();

      if (!healthCheckKey) {
        return undefined;
      }

      return {
        healthCheckKey,
        score,
        issueId,
        computedAtTimestamp:
          typeof computedAtTimestamp === 'number' && Number.isFinite(computedAtTimestamp) ? computedAtTimestamp : undefined,
      };
    })
    .filter((entry): entry is GapRow => !!entry)
    .sort((left, right) => {
      const leftHasScore = typeof left.score === 'number' && Number.isFinite(left.score);
      const rightHasScore = typeof right.score === 'number' && Number.isFinite(right.score);

      if (!leftHasScore && !rightHasScore) {
        return 0;
      }

      if (!leftHasScore) {
        return 1;
      }

      if (!rightHasScore) {
        return -1;
      }

      const leftScore = left.score as number;
      const rightScore = right.score as number;
      return rightScore - leftScore;
    });
}

function toGapRows(rows: GapRow[]): GapRow[] {
  return rows.filter((entry) => typeof entry.score === 'number' && Number.isFinite(entry.score) && entry.score < 0.9);
}

function toFailureRows(rows: GapRow[]): GapRow[] {
  return rows.filter((entry) => typeof entry.score === 'number' && Number.isFinite(entry.score) && entry.score < 0.5);
}

function toAssignedTerms(item: ClientContentItemEntity | undefined): AssignedTermModel[] {
  const assignments = item?.terms?.assigned || [];

  if (!assignments.length) {
    return [];
  }

  const terms = assignments
    .map((assignment): AssignedTermModel | undefined => {
      const termKey = toStableKey(assignment?.term);
      if (!termKey) {
        return undefined;
      }

      const facet = toTaxonomyFacet(assignment?.facet);
      return {
        termKey,
        facet,
      };
    })
    .filter((entry): entry is AssignedTermModel => !!entry);

  const deduped = new Map<string, AssignedTermModel>();
  for (const term of terms) {
    const dedupeKey = `${term.facet || ''}:${term.termKey}`;
    deduped.set(dedupeKey, term);
  }

  return Array.from(deduped.values());
}

function toTermsByFacet(terms: AssignedTermModel[], facet: TaxonomyFacet): AssignedTermModel[] {
  return terms.filter((term) => term.facet === facet);
}

function toAuthorityTerms(terms: AssignedTermModel[]): AssignedTermModel[] {
  return terms.filter(
    (term) => term.facet === TaxonomyFacet.AuthorityTopic || term.facet === TaxonomyFacet.Brand || term.facet === TaxonomyFacet.Product
  );
}

function toTargetAudienceTerms(terms: AssignedTermModel[]): AssignedTermModel[] {
  const facetPriority: TaxonomyFacet[] = [TaxonomyFacet.Persona, TaxonomyFacet.FunnelStage, TaxonomyFacet.Intent];

  return terms
    .filter((term) => term.facet !== undefined && facetPriority.includes(term.facet))
    .sort((left, right) => {
      const leftIndex = facetPriority.indexOf(left.facet as TaxonomyFacet);
      const rightIndex = facetPriority.indexOf(right.facet as TaxonomyFacet);

      if (leftIndex !== rightIndex) {
        return leftIndex - rightIndex;
      }

      return left.termKey.localeCompare(right.termKey);
    });
}

function getAuthorityFacetPriority(facet: TaxonomyFacet | undefined): number {
  if (facet === TaxonomyFacet.Brand) {
    return 0;
  }

  if (facet === TaxonomyFacet.Product) {
    return 1;
  }

  if (facet === TaxonomyFacet.AuthorityTopic) {
    return 2;
  }

  return 3;
}

function isUserAssignedKeyContent(item: ClientContentItemEntity | undefined): boolean {
  if (!item) {
    return false;
  }

  return item.contentPriority === ContentPriority.Critical && item.contentPrioritySource === ContentPrioritySource.Manual;
}

function KpiCard(props: { title: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
      <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-4">{props.title}</p>
      {props.children}
    </div>
  );
}

function ImportancePoint(props: { icon: typeof faArrowUp; color: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <FontAwesomeIcon icon={props.icon} style={{ color: props.color }} className="text-xs" />
      <span>{props.children}</span>
    </div>
  );
}

function InitiativeTextRow({ api, termKey }: { api?: ISyncCoreApi; termKey: string }) {
  const [termText, setTermText] = React.useState(termKey);
  const icon = resolveTaxonomyFacetPresentation(TaxonomyFacet.Initiative)?.icon || faArrowUp;
  const color = resolveTaxonomyFacetPresentation(TaxonomyFacet.Initiative)?.color || '#64748B';

  React.useEffect(() => {
    let active = true;

    void (async () => {
      const loaded = await loadTaxonomyTermText(api, termKey);
      if (!active) {
        return;
      }

      setTermText(loaded);
    })();

    return () => {
      active = false;
    };
  }, [api, termKey]);

  return (
    <ImportancePoint icon={icon} color={color}>
      {termText}
    </ImportancePoint>
  );
}

function GapsTable(props: {
  api?: ISyncCoreApi;
  rows: GapRow[];
  canShowAll: boolean;
  showAll: boolean;
  onShowAll: () => void;
  getPotentialGainLabel: (row: GapRow) => string;
}) {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-slate-200/70">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/70 border-b border-slate-200/70">
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Check</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Health</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-40">First Detected</th>
              <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-40 text-right">Potential Gain</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {!props.rows.length ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-sm text-slate-500">
                  No gaps detected for this item.
                </td>
              </tr>
            ) : (
              props.rows.map((row) => {
                const hasScore = typeof row.score === 'number' && Number.isFinite(row.score);
                const scorePercentage = toScorePercentage(row.score) ?? 0;
                const scorePresentation = resolveHealthCheckScorePresentation(scorePercentage);
                const scoreLabel = hasScore ? `${scorePercentage}%` : '—';
                const potentialGainLabel = props.getPotentialGainLabel(row);

                return (
                  <tr key={row.healthCheckKey} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-bold text-slate-900">
                      <HealthCheckNameReference api={props.api} healthCheckKey={row.healthCheckKey} fallback={row.healthCheckKey} />
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold" style={{ color: hasScore ? scorePresentation.style.color : undefined }}>
                      {scoreLabel}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <IssueFirstDetectedReference api={props.api} issueId={row.issueId} />
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-green-600">{potentialGainLabel}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {props.canShowAll && !props.showAll ? (
        <div className="border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline"
            onClick={props.onShowAll}
          >
            <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
            Show all checks
          </button>
        </div>
      ) : null}
    </div>
  );
}

function BrandPresenceTabs(props: {
  activeTab: DetailTabKey;
  onTabChange: (tab: DetailTabKey) => void;
  showFaqTab: boolean;
  showOptimizationTab: boolean;
}) {
  const getTabClassName = (tab: DetailTabKey) =>
    getBrandPresenceTabClassName(props.activeTab === tab, `${BRAND_PRESENCE_TAB_IN_STRIP_CLASSES} px-3 py-3.5`);

  // On a narrow screen the tabs scroll sideways in their strip. The strip reaches over the rule under
  // it, so the stroke of the tab in view still lies on the rule rather than being cut off above it.
  return (
    <div className="border-b border-slate-200/70 px-3">
      <BrandPresenceTabStrip className="-mb-px pb-px" inView={props.activeTab} role="tablist" aria-label="Content item detail tabs">
        <button
          type="button"
          className={getTabClassName('gaps')}
          onClick={() => props.onTabChange('gaps')}
          role="tab"
          aria-selected={props.activeTab === 'gaps'}
        >
          Health
        </button>
        <button
          type="button"
          className={getTabClassName('history')}
          onClick={() => props.onTabChange('history')}
          role="tab"
          aria-selected={props.activeTab === 'history'}
        >
          History
        </button>
        <button
          type="button"
          className={getTabClassName('content')}
          onClick={() => props.onTabChange('content')}
          role="tab"
          aria-selected={props.activeTab === 'content'}
        >
          Content
        </button>
        {props.showFaqTab ? (
          <button
            type="button"
            className={getTabClassName('faq')}
            onClick={() => props.onTabChange('faq')}
            role="tab"
            aria-selected={props.activeTab === 'faq'}
          >
            FAQ
          </button>
        ) : null}
        {props.showOptimizationTab ? (
          <button
            type="button"
            className={getTabClassName('optimization')}
            onClick={() => props.onTabChange('optimization')}
            role="tab"
            aria-selected={props.activeTab === 'optimization'}
          >
            Optimization
          </button>
        ) : null}
      </BrandPresenceTabStrip>
    </div>
  );
}

function OptimizationContentTabs(props: { activeTab: OptimizationContentTabKey; onTabChange: (tab: OptimizationContentTabKey) => void }) {
  const getTabClassName = (tab: OptimizationContentTabKey) => getBrandPresenceSubTabClassName(props.activeTab === tab);

  return (
    <div className="px-6 pt-5">
      <div
        className="inline-flex items-center gap-0.5 rounded-xl bg-slate-100/90 p-1"
        role="tablist"
        aria-label="Optimization content tabs"
      >
        <button type="button" className={getTabClassName('html')} onClick={() => props.onTabChange('html')} role="tab">
          HTML
        </button>
        <button type="button" className={getTabClassName('markdown')} onClick={() => props.onTabChange('markdown')} role="tab">
          Markdown
        </button>
      </div>
    </div>
  );
}

/**
 * What the page detail says about a draft the site wrote: the notice the page's tab on the site gives, titled
 * the same, saying the draft is published there, since the site publishes it and this screen offers no step to.
 */
function OptimizationSiteDraftNotice(props: { notice: ISiteDraftNotice }) {
  return (
    <div className="px-6 pt-5">
      {props.notice.inReview ? (
        <SiteDraftReviewNotice body={SITE_DRAFT_REVIEW_BODY_IN_APP_COPY} />
      ) : (
        <Alert variant={props.notice.live ? 'success' : 'info'}>{props.notice.message}</Alert>
      )}
    </div>
  );
}

function HistoryTimeline(props: {
  rows: ContentHistoryEntry[];
  loading: boolean;
  error?: string;
  hasMore: boolean;
  onLoadMore: () => void;
}) {
  if (props.loading && !props.rows.length) {
    return (
      <SkeletonRegion label="Loading the history of this page" className="space-y-4 p-6">
        {HISTORY_SKELETON}
      </SkeletonRegion>
    );
  }

  if (!props.rows.length) {
    return (
      <div className="p-6 text-sm text-slate-500">
        {props.error ? (
          <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{props.error}</div>
        ) : null}
        No history snapshots available.
      </div>
    );
  }

  return (
    <div className="p-6 motion-safe:animate-bp-fade-in">
      {props.error ? (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{props.error}</div>
      ) : null}
      <div className="space-y-4">
        {props.rows.map((row) => {
          const scoreDeltaPresentation =
            row.scoreDelta !== undefined ? resolveHealthCheckScorePresentation(toQualityDeltaScaleScore(row.scoreDelta)) : undefined;
          const scoreDeltaLabel =
            row.scoreDelta !== undefined ? `${row.scoreDelta >= 0 ? '+' : ''}${Math.round(row.scoreDelta)}% Score` : undefined;

          return (
            <div key={row.id} className="grid grid-cols-[24px,1fr] gap-4">
              <div className="flex flex-col items-center">
                <span className="mt-1.5 h-3 w-3 rounded-full bg-white ring-[3px] ring-primary/30 shadow-[inset_0_0_0_3px_#009ee3]" />
                <span className="mt-1.5 w-px flex-1 bg-slate-200" />
              </div>
              <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-4 transition-colors hover:bg-white">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-sm font-semibold text-slate-900">{row.headline}</h3>
                  <span className="text-xs text-slate-500">{toDateTimeLabel(row.timestamp)}</span>
                </div>
                <p className="text-sm text-slate-600 mt-2">{row.description}</p>
                {scoreDeltaLabel ? (
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold border mt-3"
                    style={{
                      color: scoreDeltaPresentation?.style.color,
                      backgroundColor: scoreDeltaPresentation?.style.backgroundColor,
                      borderColor: scoreDeltaPresentation?.style.color,
                    }}
                  >
                    {scoreDeltaLabel}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      {props.hasMore ? (
        <div className="mt-6 pt-4 border-t border-slate-200/80">
          <button type="button" className={BP_TOOLBAR_BUTTON_CLASSES} onClick={props.onLoadMore} disabled={props.loading}>
            {props.loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ContentMarkdownPanel(props: { markdown: string; loading: boolean; error?: string }) {
  return (
    <div className="p-6 motion-safe:animate-bp-fade-in">
      {props.loading ? (
        <SkeletonRegion label="Loading the page content" className="space-y-3">
          {TEXT_SKELETON}
        </SkeletonRegion>
      ) : null}
      {!props.loading && props.error ? <div className="text-sm text-rose-600">{props.error}</div> : null}
      {!props.loading && !props.error ? (
        <MarkdownContentBox
          markdown={props.markdown}
          emptyText="No markdown extraction available."
          containerClassName="m-0"
          contentClassName={`${DEFAULT_MARKDOWN_CONTENT_CLASS_NAME} [&_img]:max-w-[300px] [&_img]:h-auto`}
        />
      ) : null}
    </div>
  );
}

/** One address of a content area, and the page the content area belongs to. */
interface IAddressOfPage {
  address: string;
  /** The page's own address: an address naming no host names a file of the page's site. */
  pageUrl: string;
}

/** An image source set of a content area, and the page the content area belongs to. */
interface ISourceSetOfPage {
  sourceSet: string;
  /** The page's own address: an address naming no host names a file of the page's site. */
  pageUrl: string;
}

/**
 * An address as the page's site serves it: one naming no host is read against the page's own address.
 * An empty address names nothing and one that is only a fragment names a place in the same content, so
 * both stay as they are.
 */
function toSiteAddress({ address, pageUrl }: IAddressOfPage): string {
  const written = address.trim();
  return !written || written.startsWith('#') ? address : toAbsoluteUrl(written, pageUrl);
}

/**
 * Every candidate of a source set as the page's site serves it. A candidate is an address and the width
 * or density it stands for, and candidates are separated by commas; an address may carry commas of its
 * own, so it ends at the first whitespace, or at the commas it ends with.
 */
function toSiteSourceSet({ sourceSet, pageUrl }: ISourceSetOfPage): string {
  const candidates: string[] = [];
  let rest = sourceSet.replace(/^[\s,]+/, '');

  while (rest) {
    const address = rest.match(/^\S+/)?.[0] ?? '';
    rest = rest.slice(address.length);

    if (address.endsWith(',')) {
      candidates.push(toSiteAddress({ address: address.replace(/,+$/, ''), pageUrl }));
    } else {
      const descriptorEnd = rest.indexOf(',');
      const descriptor = (descriptorEnd === -1 ? rest : rest.slice(0, descriptorEnd)).trim();
      rest = descriptorEnd === -1 ? '' : rest.slice(descriptorEnd + 1);
      candidates.push([toSiteAddress({ address, pageUrl }), descriptor].filter(Boolean).join(' '));
    }

    rest = rest.replace(/^[\s,]+/, '');
  }

  return candidates.join(', ');
}

/** A content area as the comparison shows it. */
interface IComparedContentArea {
  html: string;
  /** The page's own address, when the page names one. */
  pageUrl?: string;
}

/**
 * The attributes of a content area's elements that hold one address each: a link's, an embedded file's,
 * a video's still, a form's target, a legacy background's, and an object's data.
 */
const CONTENT_ADDRESS_ATTRIBUTES: ReadonlyArray<{ selector: string; attribute: string }> = [
  { selector: '[href]', attribute: 'href' },
  { selector: '[src]', attribute: 'src' },
  { selector: '[poster]', attribute: 'poster' },
  { selector: '[action]', attribute: 'action' },
  { selector: '[background]', attribute: 'background' },
  { selector: 'object[data]', attribute: 'data' },
];

/**
 * A content area as the comparison shows it: every image at most 300 pixels wide, and every address in
 * it as the page's site serves it. A content area carries its addresses as the site wrote them, and one
 * naming no host names a file or a page of the site, which the host this screen is served from does
 * not have: that host answers every path with this screen itself, so a frame or a video left as the
 * site wrote it would load this screen rather than the site's file. The comparison shows the images,
 * and opens the links, of the site; whether it also plays the site's media, frames and objects is for
 * this screen's content policy to allow. The area is read into a document of its own, which loads none
 * of its images.
 */
function toComparedHtml({ html, pageUrl }: IComparedContentArea): string {
  if (!html) {
    return html;
  }

  const area = new DOMParser().parseFromString(html, 'text/html').body;

  area.querySelectorAll('img').forEach((image) => {
    image.style.maxWidth = '300px';
    image.style.height = 'auto';
    image.removeAttribute('width');
  });

  if (pageUrl) {
    CONTENT_ADDRESS_ATTRIBUTES.forEach(({ selector, attribute }) => {
      area.querySelectorAll(selector).forEach((element) => {
        element.setAttribute(attribute, toSiteAddress({ address: element.getAttribute(attribute) ?? '', pageUrl }));
      });
    });
    area.querySelectorAll('[srcset]').forEach((element) => {
      element.setAttribute('srcset', toSiteSourceSet({ sourceSet: element.getAttribute('srcset') ?? '', pageUrl }));
    });
  }

  return area.innerHTML;
}

function OptimizationDiffPanel(props: {
  sourceHtml: string;
  targetHtml: string;
  /** The page's own address, which the addresses of both content areas are read against. */
  pageUrl?: string;
  loading: boolean;
  error?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (props.loading || props.error || !containerRef.current) {
      return;
    }

    const renderer = window.ContentSyncContentDiffRenderer;
    if (!renderer) {
      return;
    }

    const sourceHtml = toComparedHtml({ html: props.sourceHtml, pageUrl: props.pageUrl });
    const targetHtml = toComparedHtml({ html: props.targetHtml, pageUrl: props.pageUrl });

    renderer.renderDiff({
      container: containerRef.current,
      sourceHtml,
      targetHtml,
      interactive: false,
    });
  }, [props.loading, props.error, props.sourceHtml, props.targetHtml, props.pageUrl]);

  return (
    <div className="p-6 motion-safe:animate-bp-fade-in">
      {props.loading ? (
        <SkeletonRegion label="Loading the comparison" className="space-y-3">
          {TEXT_SKELETON}
        </SkeletonRegion>
      ) : null}
      {!props.loading && props.error ? <div className="text-sm text-rose-600">{props.error}</div> : null}
      {!props.loading && !props.error ? (
        <div ref={containerRef} className="governance-optimization-diff [&_img]:max-w-[300px] [&_img]:h-auto" />
      ) : null}
    </div>
  );
}

function OptimizationMarkdownDiffPanel(props: {
  sourceMarkdown: string;
  targetMarkdown: string;
  loading: boolean;
  error?: string;
  mode: OptimizationMarkdownMode;
  onModeChange: (mode: OptimizationMarkdownMode) => void;
}) {
  const sourceRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const visualDiffContainerRef = useRef<HTMLDivElement | null>(null);

  // ReactMarkdown renders synchronously into the hidden divs; useLayoutEffect
  // fires right after the DOM commit so we can safely read innerHTML here.
  useLayoutEffect(() => {
    if (
      props.mode !== 'visual' ||
      props.loading ||
      props.error ||
      !sourceRef.current ||
      !targetRef.current ||
      !visualDiffContainerRef.current
    ) {
      return;
    }

    const renderer = window.ContentSyncContentDiffRenderer;
    if (!renderer) {
      return;
    }

    renderer.renderDiff({
      container: visualDiffContainerRef.current,
      sourceHtml: sourceRef.current.innerHTML,
      targetHtml: targetRef.current.innerHTML,
      interactive: false,
    });
  }, [props.mode, props.loading, props.error, props.sourceMarkdown, props.targetMarkdown]);

  return (
    <div className="p-6 overflow-x-auto motion-safe:animate-bp-fade-in">
      <div className="mb-4">
        <SwitchButton
          options={{ visual: 'Visual', technical: 'Technical' } as Record<OptimizationMarkdownMode, string>}
          selected={props.mode}
          onSelect={props.onModeChange}
          size="sm"
        />
      </div>
      {props.loading ? (
        <SkeletonRegion label="Loading the comparison" className="space-y-3">
          {TEXT_SKELETON}
        </SkeletonRegion>
      ) : null}
      {!props.loading && props.error ? <div className="text-sm text-rose-600">{props.error}</div> : null}
      {!props.loading && !props.error && props.mode === 'visual' ? (
        <>
          {/* Hidden source markdown rendered to DOM so the diff renderer can read its HTML */}
          <div ref={sourceRef} className="hidden" aria-hidden="true">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{props.sourceMarkdown}</ReactMarkdown>
          </div>
          {/* Hidden target markdown rendered to DOM so the diff renderer can read its HTML */}
          <div ref={targetRef} className="hidden" aria-hidden="true">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{props.targetMarkdown}</ReactMarkdown>
          </div>
          <div ref={visualDiffContainerRef} className="governance-optimization-diff [&_img]:max-w-[300px] [&_img]:h-auto" />
        </>
      ) : null}
      {!props.loading && !props.error && props.mode === 'technical' ? (
        <ReactDiffViewer oldValue={props.sourceMarkdown} newValue={props.targetMarkdown} />
      ) : null}
    </div>
  );
}

export function GovernanceContentInventoryDetail() {
  const { key: routeKey, statusOrId: routeStatusOrId } = useParams<{ key: string; statusOrId?: string }>();
  const navigate = useNavigate();
  const apiContext = useContext(SyncCoreApiContext);
  const api = apiContext?.api;
  // The content list's filters travel on the address, handed over with the path; the frame's own
  // address only fills the moment right after the in-frame navigation that opened this page. Back
  // carries this search on, so the list reopens as the reader left it.
  const screenAddress = useBrandPresenceScreenAddress();
  const screenSearch = searchOfBrandPresenceScreenAddress(screenAddress);

  const decodedKey = useMemo(() => decodeContentItemKey(routeKey), [routeKey]);
  const decodedStatusOrId = useMemo(() => decodeContentItemKey(routeStatusOrId), [routeStatusOrId]);
  const routeStatus = useMemo(() => brandPresenceContentItemStatusFromSegment(decodedStatusOrId), [decodedStatusOrId]);
  const [item, setItem] = useState<ClientContentItemEntity | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [showAllChecks, setShowAllChecks] = useState(false);
  const [weightByHealthCheckKey, setWeightByHealthCheckKey] = useState<Record<string, number>>({});
  const [authorityTermNameByKey, setAuthorityTermNameByKey] = useState<Record<string, string>>({});
  const [averageQualityScore, setAverageQualityScore] = useState<number | undefined>(undefined);
  // A link can address this screen at its Health tab with one check named, which is then
  // highlighted and pre-selected. Both travel in the address the host page mirrors; the Health tab
  // is also where the screen opens, so the check is honoured exactly when the address names that
  // tab or names none.
  const screenParams = useMemo(() => new URLSearchParams(screenSearch), [screenSearch]);
  const requestedTab = screenParams.get(BRAND_PRESENCE_CONTENT_ITEM_QUERY_PARAMS.tab)?.trim();
  const addressesHealthTab = !requestedTab || requestedTab === BRAND_PRESENCE_CONTENT_ITEM_HEALTH_TAB_TOKEN;
  const highlightedHealthCheckKey = addressesHealthTab
    ? screenParams.get(BRAND_PRESENCE_CONTENT_ITEM_QUERY_PARAMS.check)?.trim() || undefined
    : undefined;
  const [activeTab, setActiveTab] = useState<DetailTabKey>('gaps');
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [markdownError, setMarkdownError] = useState<string | undefined>(undefined);
  const [historyRevisions, setHistoryRevisions] = useState<ClientContentItemEntity[]>([]);
  const [historyNextPage, setHistoryNextPage] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | undefined>(undefined);
  const [optimizationSourceHtml, setOptimizationSourceHtml] = useState<string>('');
  const [optimizationTargetHtml, setOptimizationTargetHtml] = useState<string>('');
  const [optimizationSourceMarkdown, setOptimizationSourceMarkdown] = useState<string>('');
  const [optimizationTargetMarkdown, setOptimizationTargetMarkdown] = useState<string>('');
  const [optimizationLoading, setOptimizationLoading] = useState(false);
  const [optimizationError, setOptimizationError] = useState<string | undefined>(undefined);
  const [optimizationMarkdownError, setOptimizationMarkdownError] = useState<string | undefined>(undefined);
  const [hasOptimizationSuggestion, setHasOptimizationSuggestion] = useState(false);
  // What the page's tab on the site says about the draft, when the site wrote it.
  const [optimizationSiteDraftNotice, setOptimizationSiteDraftNotice] = useState<ISiteDraftNotice | undefined>(undefined);
  // Adding a FAQ to the page creates a draft, so the probe that decides whether
  // the Optimization tab is on the bar runs again afterwards.
  const [optimizationProbeCount, setOptimizationProbeCount] = useState(0);
  // Bumped exactly when a draft first appears, so the comparison is loaded for it.
  const [optimizationPayloadReload, setOptimizationPayloadReload] = useState(0);
  const hasOptimizationSuggestionRef = useRef(false);
  const [optimizationContentTab, setOptimizationContentTab] = useState<OptimizationContentTabKey>('html');
  const [optimizationMarkdownMode, setOptimizationMarkdownMode] = useState<OptimizationMarkdownMode>('visual');

  // The page's content item already carries how many Q&A pairs the page has and
  // how many FAQs are suggested for it, so the FAQ tab's visibility is read
  // straight off it without another request.
  const qaPairCount = item?.extractions?.[ContentExtractionType.QaPairs]?.count;
  const suggestedFaqCount = item?.recommendations?.[ContentRecommendationKind.Faq]?.count;
  const showFaqTab =
    (typeof qaPairCount === 'number' && qaPairCount > 0) || (typeof suggestedFaqCount === 'number' && suggestedFaqCount > 0);

  const probeForOptimizationDraft = useCallback(() => setOptimizationProbeCount((count) => count + 1), []);

  const faq = useSuggestedFaqs({
    api,
    item,
    itemId: toInternalId(item?.id),
    open: activeTab === 'faq',
    onDraftProbe: probeForOptimizationDraft,
  });

  useEffect(() => {
    if (!decodedKey || decodedStatusOrId) {
      return;
    }

    navigate(`${brandPresenceContentItemPath({ key: decodedKey, status: ContentItemStatus.Published })}${screenSearch}`, {
      replace: true,
    });
  }, [decodedKey, decodedStatusOrId, navigate, screenSearch]);

  useEffect(() => {
    if (!decodedStatusOrId) {
      return;
    }

    if (!api || !decodedKey) {
      setLoading(false);
      if (!decodedKey) {
        setErrorText('Invalid content item key.');
      }
      return;
    }

    let active = true;

    void (async () => {
      setLoading(true);
      setErrorText(undefined);
      setShowAllChecks(false);

      try {
        let loadedItem: ClientContentItemEntity | null = null;

        if (routeStatus) {
          const selected = await api.governance.contentItems.itemByKey(decodedKey, routeStatus);
          if (!selected) {
            if (!active) {
              return;
            }

            setItem(undefined);
            setErrorText('Content item not found.');
            setLoading(false);
            return;
          }

          loadedItem = await api.governance.contentItems.item(selected.id);
        } else {
          const revisionId = toInternalEntityId(decodedStatusOrId);
          if (!revisionId) {
            if (!active) {
              return;
            }

            setItem(undefined);
            setErrorText('Invalid content item revision ID.');
            setLoading(false);
            return;
          }

          loadedItem = await api.governance.contentItems.item(revisionId);
        }

        if (!active) {
          return;
        }

        if (!loadedItem) {
          setItem(undefined);
          setErrorText(routeStatus ? 'Content item not found.' : 'Content item revision not found.');
          setLoading(false);
          return;
        }

        setItem(loadedItem);
        setLoading(false);
      } catch {
        if (!active) {
          return;
        }

        setItem(undefined);
        setErrorText('Failed to load content item details.');
        setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [api, decodedKey, decodedStatusOrId, routeStatus]);

  useEffect(() => {
    if (!api) {
      setAverageQualityScore(undefined);
      return;
    }

    let active = true;

    void (async () => {
      try {
        // The average rides in the list's envelope, so one row is asked for: the content list refuses a
        // page of none.
        const summary = await api.governance.contentItems.list(COUNT_ONLY);
        if (!active) {
          return;
        }

        setAverageQualityScore(summary.averageHealthScore === null ? undefined : summary.averageHealthScore);
      } catch {
        if (!active) {
          return;
        }

        setAverageQualityScore(undefined);
      }
    })();

    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    if (!hasOptimizationSuggestion && activeTab === 'optimization') {
      setActiveTab('content');
    }
  }, [activeTab, hasOptimizationSuggestion]);

  useEffect(() => {
    if (!showFaqTab && activeTab === 'faq') {
      setActiveTab('content');
    }
  }, [activeTab, showFaqTab]);

  // Asking again whether the page now has a draft is its own effect, so adding
  // a FAQ re-runs only this one read and not the optimization bodies below. It
  // runs after an add, never on the first render, and only ever puts the
  // Optimization tab on the bar — taking it off stays the payload effect's.
  useEffect(() => {
    if (!api || !item || optimizationProbeCount === 0 || item.status !== ContentItemStatus.Published) {
      return;
    }

    let active = true;

    void (async () => {
      const suggestionItem = await loadDraftOfOptimizedPage(api, { pageKey: item.key }).catch(() => undefined);
      if (!active || !suggestionItem) {
        return;
      }

      // The draft has just arrived, so the comparison below is loaded once — by
      // this one event, not by every add. The decision is taken here rather
      // than inside the state updater, which must stay pure.
      if (!hasOptimizationSuggestionRef.current) {
        setOptimizationPayloadReload((count) => count + 1);
      }

      hasOptimizationSuggestionRef.current = true;
      setHasOptimizationSuggestion(true);
    })();

    return () => {
      active = false;
    };
  }, [api, item, optimizationProbeCount]);

  useEffect(() => {
    setOptimizationContentTab('html');
    setOptimizationMarkdownMode('visual');
  }, [item?.id]);

  useEffect(() => {
    if (!api || !item || item.status !== ContentItemStatus.Published) {
      setHasOptimizationSuggestion(false);
      hasOptimizationSuggestionRef.current = false;
      setOptimizationSiteDraftNotice(undefined);
      setOptimizationSourceHtml('');
      setOptimizationTargetHtml('');
      setOptimizationSourceMarkdown('');
      setOptimizationTargetMarkdown('');
      setOptimizationError(undefined);
      setOptimizationMarkdownError(undefined);
      setOptimizationLoading(false);
      return;
    }

    let active = true;

    void (async () => {
      setOptimizationLoading(true);
      setOptimizationError(undefined);

      try {
        const suggestionItem = await loadSuggestionRevisionByKey(api, item.key);
        if (!active) {
          return;
        }

        if (!suggestionItem) {
          setHasOptimizationSuggestion(false);
          hasOptimizationSuggestionRef.current = false;
          setOptimizationSiteDraftNotice(undefined);
          setOptimizationSourceHtml('');
          setOptimizationTargetHtml('');
          setOptimizationSourceMarkdown('');
          setOptimizationTargetMarkdown('');
          setOptimizationMarkdownError(undefined);
          setOptimizationLoading(false);
          return;
        }

        // The optimization that produced the draft says whether the site wrote it, and what became of it
        // there: the page's tab on the site reads the same one, and shows its review once it has. It adds
        // only that line, so a failed read still shows the comparison, without the line.
        const suggestionRevisionId = toInternalEntityId(suggestionItem.id);
        const [baseHtml, suggestionHtml, markdownResults, optimization] = await Promise.all([
          loadHtmlContentByRevisionId(api, item),
          loadHtmlContentByRevisionId(api, suggestionItem),
          Promise.allSettled([loadMarkdownContentByExtraction(item), loadMarkdownContentByExtraction(suggestionItem)]),
          suggestionRevisionId
            ? loadOptimizationForSuggestion(api, { suggestionRevisionId, pageKey: item.key }).catch(() => undefined)
            : undefined,
        ]);

        if (!active) {
          return;
        }

        const siteDraftNotice = toSiteDraftNotice(optimization);
        if (!baseHtml.trim() || !suggestionHtml.trim()) {
          setHasOptimizationSuggestion(true);
          hasOptimizationSuggestionRef.current = true;
          setOptimizationSiteDraftNotice(siteDraftNotice);
          setOptimizationSourceHtml(baseHtml);
          setOptimizationTargetHtml(suggestionHtml);
          setOptimizationSourceMarkdown('');
          setOptimizationTargetMarkdown('');
          setOptimizationMarkdownError('Optimization markdown extraction is not available for one or both revisions.');
          setOptimizationError('Optimization HTML extraction is not available for one or both revisions.');
          setOptimizationLoading(false);
          return;
        }

        const [baseMarkdownResult, suggestionMarkdownResult] = markdownResults;
        const baseMarkdown = baseMarkdownResult.status === 'fulfilled' ? baseMarkdownResult.value : '';
        const suggestionMarkdown = suggestionMarkdownResult.status === 'fulfilled' ? suggestionMarkdownResult.value : '';
        const markdownFailed = baseMarkdownResult.status === 'rejected' || suggestionMarkdownResult.status === 'rejected';
        // A draft the site wrote is its own rendering of the page, in wrappers of its own around the same
        // blocks, and a draft Sync Core generated keeps the page's wrappers: both content areas are read
        // through the one routine that meets them block by block, whichever wrote the draft.
        const comparable = toComparableContents({ sourceHtml: baseHtml, targetHtml: suggestionHtml });

        setHasOptimizationSuggestion(true);
        hasOptimizationSuggestionRef.current = true;
        setOptimizationSiteDraftNotice(siteDraftNotice);
        setOptimizationSourceHtml(comparable.sourceHtml);
        setOptimizationTargetHtml(comparable.targetHtml);
        setOptimizationSourceMarkdown(baseMarkdown);
        setOptimizationTargetMarkdown(suggestionMarkdown);
        setOptimizationMarkdownError(
          markdownFailed ? 'Optimization markdown extraction is not available for one or both revisions.' : undefined
        );
        setOptimizationError(undefined);
        setOptimizationLoading(false);
      } catch {
        if (!active) {
          return;
        }

        setHasOptimizationSuggestion(false);
        hasOptimizationSuggestionRef.current = false;
        setOptimizationSiteDraftNotice(undefined);
        setOptimizationSourceHtml('');
        setOptimizationTargetHtml('');
        setOptimizationSourceMarkdown('');
        setOptimizationTargetMarkdown('');
        setOptimizationError('Failed to load optimization diff.');
        setOptimizationMarkdownError(undefined);
        setOptimizationLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [api, item, optimizationPayloadReload]);

  const qualityScore = toScorePercentage(item?.healthScore);
  const averageQualityPercentage = toScorePercentage(averageQualityScore);
  const qualityDeltaPercentage =
    qualityScore !== undefined && averageQualityPercentage !== undefined ? qualityScore - averageQualityPercentage : undefined;
  const qualityDeltaPresentation =
    qualityDeltaPercentage !== undefined
      ? resolveHealthCheckScorePresentation(toQualityDeltaScaleScore(qualityDeltaPercentage))
      : undefined;
  const qualityDeltaLabel =
    qualityDeltaPercentage !== undefined
      ? `${Math.abs(qualityDeltaPercentage)}% ${qualityDeltaPercentage >= 0 ? 'above' : 'below'} average`
      : '-';
  const importance = translateEnumSafe('ContentPriority', item?.contentPriority as ContentPriority | undefined);
  const itemName = toText(item?.name, decodedKey || 'Content item');
  const figuresUpdated = figuresUpdatedTimestamp(item);
  const itemUrl = typeof item?.url === 'string' && item.url.trim().length > 0 ? item.url : undefined;
  const assignedTerms = toAssignedTerms(item);
  const authorityTerms = toAuthorityTerms(assignedTerms);
  const targetAudienceTerms = toTargetAudienceTerms(assignedTerms);
  const authorityTopicTerms = toTermsByFacet(assignedTerms, TaxonomyFacet.AuthorityTopic);
  const initiativeTerms = toTermsByFacet(assignedTerms, TaxonomyFacet.Initiative);
  const authorityTermKeys = useMemo(() => Array.from(new Set(authorityTerms.map((term) => term.termKey))), [authorityTerms]);
  const authorityTermKeysSignature = useMemo(() => authorityTermKeys.join('|'), [authorityTermKeys]);

  useEffect(() => {
    if (!api || !authorityTermKeys.length) {
      setAuthorityTermNameByKey({});
      return;
    }

    let active = true;

    void (async () => {
      const entries = await Promise.all(
        authorityTermKeys.map(async (termKey): Promise<[string, string]> => {
          const loadedName = await loadTaxonomyTermText(api, termKey);
          return [termKey, loadedName || termKey];
        })
      );

      if (!active) {
        return;
      }

      const nextAuthorityTermNameByKey = Object.fromEntries(entries);
      setAuthorityTermNameByKey((previous) =>
        areStringRecordsEqual(previous, nextAuthorityTermNameByKey) ? previous : nextAuthorityTermNameByKey
      );
    })();

    return () => {
      active = false;
    };
  }, [api, authorityTermKeysSignature]);

  const sortedAuthorityTerms = useMemo(() => {
    return [...authorityTerms].sort((left, right) => {
      const facetPriorityDiff = getAuthorityFacetPriority(left.facet) - getAuthorityFacetPriority(right.facet);
      if (facetPriorityDiff !== 0) {
        return facetPriorityDiff;
      }

      const leftName = (authorityTermNameByKey[left.termKey] || left.termKey).toLowerCase();
      const rightName = (authorityTermNameByKey[right.termKey] || right.termKey).toLowerCase();
      return leftName.localeCompare(rightName);
    });
  }, [authorityTerms, authorityTermNameByKey]);
  const isKeyContent = isUserAssignedKeyContent(item);
  const allHealthRows = toAllHealthRows(item);
  const allHealthCheckKeys = useMemo(() => Array.from(new Set(allHealthRows.map((row) => row.healthCheckKey))), [allHealthRows]);
  const allHealthCheckKeysSignature = useMemo(() => allHealthCheckKeys.join('|'), [allHealthCheckKeys]);

  useEffect(() => {
    if (!api || !allHealthCheckKeys.length) {
      setWeightByHealthCheckKey({});
      return;
    }

    let active = true;

    void (async () => {
      const entries = await Promise.all(
        allHealthCheckKeys.map(async (healthCheckKey): Promise<[string, number]> => {
          const referenceData = await loadHealthCheckReferenceDataByKey(api, healthCheckKey);
          return [healthCheckKey, referenceData.defaultScoreWeight || 1];
        })
      );

      if (!active) {
        return;
      }

      const nextWeightByHealthCheckKey = Object.fromEntries(entries);
      setWeightByHealthCheckKey((previous) =>
        areNumberRecordsEqual(previous, nextWeightByHealthCheckKey) ? previous : nextWeightByHealthCheckKey
      );
    })();

    return () => {
      active = false;
    };
  }, [api, allHealthCheckKeysSignature]);

  const totalScoreWeight = useMemo(() => {
    return allHealthRows.reduce((sum, row) => {
      if (typeof row.score !== 'number' || !Number.isFinite(row.score)) {
        return sum;
      }

      const weight = weightByHealthCheckKey[row.healthCheckKey] ?? 1;
      return sum + weight;
    }, 0);
  }, [allHealthRows, weightByHealthCheckKey]);

  const getPotentialGainLabel = useMemo(() => {
    return (row: GapRow): string => {
      if (typeof row.score !== 'number' || !Number.isFinite(row.score) || row.score === 1 || totalScoreWeight <= 0) {
        return '—';
      }

      const rowWeight = weightByHealthCheckKey[row.healthCheckKey] ?? 1;
      const differenceToHundred = (1 - Math.max(0, Math.min(1, row.score))) * 100;
      const weightedPotentialGain = (differenceToHundred * rowWeight) / totalScoreWeight;

      if (!Number.isFinite(weightedPotentialGain) || weightedPotentialGain <= 0) {
        return '—';
      }

      const roundedGain = Math.round(weightedPotentialGain * 10) / 10;
      if (roundedGain <= 0) {
        return '—';
      }

      return `+${roundedGain}%`;
    };
  }, [totalScoreWeight, weightByHealthCheckKey]);

  const gapRows = toGapRows(allHealthRows);
  const visibleGapRows = showAllChecks ? allHealthRows : gapRows;
  const failureRows = toFailureRows(allHealthRows);
  const historyRows = useMemo(() => buildContentHistoryRows(item, historyRevisions), [item, historyRevisions]);

  useEffect(() => {
    if (!api || !decodedKey || !item) {
      setHistoryRevisions([]);
      setHistoryNextPage(0);
      setHistoryHasMore(false);
      setHistoryLoading(false);
      setHistoryError(undefined);
      return;
    }

    let active = true;

    void (async () => {
      setHistoryLoading(true);
      setHistoryError(undefined);

      try {
        const response = await loadContentItemRevisionHistoryPage(api, item.key, 0);
        if (!active) {
          return;
        }

        setHistoryRevisions(response.matches);
        setHistoryNextPage(response.nextPage);
        setHistoryHasMore(response.hasMore);
        setHistoryLoading(false);
      } catch {
        if (!active) {
          return;
        }

        setHistoryRevisions([]);
        setHistoryNextPage(0);
        setHistoryHasMore(false);
        setHistoryLoading(false);
        setHistoryError('Failed to load history snapshots.');
      }
    })();

    return () => {
      active = false;
    };
  }, [api, decodedKey, item]);

  const loadMoreHistory = async () => {
    if (!api || !item || !historyHasMore || historyLoading) {
      return;
    }

    setHistoryLoading(true);
    setHistoryError(undefined);

    try {
      const response = await loadContentItemRevisionHistoryPage(api, item.key, historyNextPage);
      setHistoryRevisions((previous) => appendUniqueContentItems(previous, response.matches));
      setHistoryNextPage(response.nextPage);
      setHistoryHasMore(response.hasMore);
      setHistoryLoading(false);
    } catch {
      setHistoryLoading(false);
      setHistoryError('Failed to load more history snapshots.');
    }
  };

  useEffect(() => {
    if (!api || !item) {
      setMarkdownContent('');
      setMarkdownError(undefined);
      setMarkdownLoading(false);
      return;
    }

    let active = true;

    setMarkdownLoading(true);
    loadMarkdownContentByExtraction(item)
      .then((markdown) => {
        if (!active) {
          return;
        }

        setMarkdownContent(markdown);
        setMarkdownLoading(false);
      })
      .catch((e) => {
        if (!active) {
          return;
        }

        console.error('Failed to load markdown extraction file', e);

        setMarkdownContent('');
        setMarkdownError(e instanceof Error && e.message ? e.message : 'Failed to load markdown extraction.');
        setMarkdownLoading(false);
      });

    return () => {
      active = false;
    };
  }, [api, item]);

  if (loading) {
    return <ContentItemDetailSkeleton />;
  }

  const backToInventory = () => navigate(`${BRAND_PRESENCE_CONTENT_INVENTORY_PATH}${screenSearch}`);

  return (
    <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
      <main className="relative max-w-[1600px] mx-auto px-6 py-8 space-y-6">
        <header className="motion-safe:animate-bp-fade-up">
          <button
            type="button"
            className="group/back -ml-2 mb-4 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-semibold text-slate-500 transition-colors hover:bg-white/70 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={backToInventory}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-xs transition-transform duration-150 group-hover/back:-translate-x-0.5" />
            Back to Content Inventory
          </button>
          <BrandPresenceEyebrow>
            Brand Presence <span className="text-slate-300">/</span> Page
          </BrandPresenceEyebrow>
          <h1
            className="m-0 flex min-w-0 items-center gap-3 text-[1.75rem] leading-tight font-bold tracking-tight text-slate-900"
            title={itemName}
          >
            <ContentItemStatusIcon status={item?.status} className="text-xl" />
            <span className="truncate">{itemName}</span>
          </h1>
          {itemUrl ? <p className="mt-2 mb-0 truncate font-eyebrow text-xs text-slate-400">{itemUrl}</p> : null}
        </header>

        {errorText ? (
          <div className={BP_CARD_CLASSES}>
            <BrandPresenceEmptyState
              icon={faTriangleExclamation}
              title={errorText}
              description="The page may have been removed from your inventory. Pick it again from the list."
              actions={
                <button type="button" className={BP_TOOLBAR_BUTTON_CLASSES} onClick={backToInventory}>
                  Back to Content Inventory
                </button>
              }
            />
          </div>
        ) : null}

        {!errorText ? (
          <>
            <ContentItemKpiCards api={api} item={item} averageQualityScore={averageQualityScore} />

            <ContentItemPageFigures api={api} item={item} />

            {/* What the cards above show, dated — so a figure that has stood still for a month is read
                as a month-old figure rather than as today's. */}
            {figuresUpdated ? <p className="text-xs text-slate-500 mb-0">Figures updated {toDateTimeLabel(figuresUpdated)}</p> : null}

            {/* One follow per draft this visit asked for; they stop with the screen. */}
            {api
              ? faq.follows.map((follow) => (
                  <FaqDraftFollower
                    key={follow.suggestionId}
                    api={api}
                    taskGroupId={follow.taskGroupId}
                    onState={(state) => faq.onDraftState({ suggestionId: follow.suggestionId, state })}
                  />
                ))
              : null}

            <section className={`${BP_CARD_CLASSES} motion-safe:animate-bp-fade-up [--bp-delay:160ms]`}>
              <BrandPresenceTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                showFaqTab={showFaqTab}
                showOptimizationTab={hasOptimizationSuggestion}
              />
              {activeTab === 'gaps' ? (
                <ContentItemGapsTable api={api} item={item} highlightHealthCheckKey={highlightedHealthCheckKey} />
              ) : null}
              {activeTab === 'history' ? (
                <HistoryTimeline
                  rows={historyRows}
                  loading={historyLoading}
                  error={historyError}
                  hasMore={historyHasMore}
                  onLoadMore={() => {
                    void loadMoreHistory();
                  }}
                />
              ) : null}
              {activeTab === 'content' ? (
                <ContentMarkdownPanel markdown={markdownContent} loading={markdownLoading} error={markdownError} />
              ) : null}
              {activeTab === 'faq' ? (
                <FaqPanel
                  pairs={faq.pairs}
                  expectedCount={qaPairCount}
                  loading={faq.pairsLoading}
                  error={faq.pairsError}
                  onRetry={() => {
                    void faq.reloadPairs();
                  }}
                  suggested={{
                    state: faq.state,
                    onRetry: () => {
                      void faq.reloadSuggestions();
                    },
                    suggestions: faq.suggestions,
                    rowStates: faq.rowStates,
                    pageKey: `${item?.key ?? ''}`,
                    resolveTerm: faq.resolveTerm,
                    onOpenOptimizationTab: () => setActiveTab('optimization'),
                    canOpenOptimizationTab: hasOptimizationSuggestion,
                    draftStates: faq.draftStates,
                    capped: faq.capped,
                    addedStateIncomplete: faq.addedStateIncomplete,
                    onAdd: faq.add,
                    onDismiss: faq.dismiss,
                    announcement: faq.announcement,
                    announcementKey: faq.announcementKey,
                    onAnnounce: faq.announce,
                  }}
                />
              ) : null}
              {activeTab === 'optimization' ? (
                <>
                  {!optimizationLoading && optimizationSiteDraftNotice ? (
                    <OptimizationSiteDraftNotice notice={optimizationSiteDraftNotice} />
                  ) : null}
                  <OptimizationContentTabs activeTab={optimizationContentTab} onTabChange={setOptimizationContentTab} />
                  {optimizationContentTab === 'html' ? (
                    <OptimizationDiffPanel
                      sourceHtml={optimizationSourceHtml}
                      targetHtml={optimizationTargetHtml}
                      pageUrl={item?.canonicalUrl || item?.url}
                      loading={optimizationLoading}
                      error={optimizationError}
                    />
                  ) : (
                    <OptimizationMarkdownDiffPanel
                      sourceMarkdown={optimizationSourceMarkdown}
                      targetMarkdown={optimizationTargetMarkdown}
                      loading={optimizationLoading}
                      error={optimizationMarkdownError}
                      mode={optimizationMarkdownMode}
                      onModeChange={setOptimizationMarkdownMode}
                    />
                  )}
                </>
              ) : null}
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}
